import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject, Signal } from '@angular/core';
import { LinePrefillRequest, PrefillActions, PrefilledLine } from '@models/products-and-orders/lines-prefill/lines-prefill.model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { createStateResetHandler, sortAlphabetically } from '../../helpers';
import { LinesPrefillService } from './lines-prefill.service';

type LinesPrefillState = {
  period: { from: string; to: string } | null;
  actions: PrefillActions[];
  lines: PrefilledLine[];
  isLoading: boolean;
};

const initialState: LinesPrefillState = {
  period: null,
  actions: [],
  lines: [],
  isLoading: false,
};

export const LinesPrefillStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('lines-prefill-store'),

  withMethods((store, linesPrefillService = inject(LinesPrefillService), notificationsService = inject(NotificationsService)) => {
    createStateResetHandler<LinesPrefillState>('/lines-prefill')(store, initialState);

    const repositoryMethods = {
      getAll: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            linesPrefillService.getAll().pipe(
              tap(response => {
                if (response && response.rows.length) {
                  const sortedRows = sortAlphabetically(response.rows, (a, b) => {
                    return a.customerName.localeCompare(b.customerName);
                  });

                  patchState(store, {
                    lines: sortedRows,
                    period: { from: response.periodFrom, to: response.periodTo },
                    actions: response.actions,
                    isLoading: false,
                  });
                } else {
                  patchState(store, { isLoading: false });
                }
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      create: rxMethod<{ data: LinePrefillRequest }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ data }) =>
            linesPrefillService.create(data).pipe(
              tap(response => {
                const sortedRows = sortAlphabetically(response.rows, (a, b) => {
                  return a.customerName.localeCompare(b.customerName);
                });
                patchState(store, {
                  lines: sortedRows,
                  period: { from: response.periodFrom, to: response.periodTo },
                  actions: response.actions,
                  isLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      validate: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            linesPrefillService.validate().pipe(
              tap(response => {
                const sortedRows = sortAlphabetically(response.rows, (a, b) => {
                  return a.customerName.localeCompare(b.customerName);
                });
                patchState(store, {
                  lines: sortedRows,
                  period: { from: response.periodFrom, to: response.periodTo },
                  actions: response.actions,
                  isLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      apply: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            linesPrefillService.apply().pipe(
              tap(() => {
                patchState(store, {
                  lines: [],
                  period: null,
                  actions: [],
                  isLoading: false,
                });

                notificationsService.showNotification('Lines have been prefilled successfully.');
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      removeAll: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            linesPrefillService.removeAll().pipe(
              tap(() => {
                patchState(store, {
                  lines: [],
                  period: null,
                  actions: [],
                  isLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      remove: rxMethod<{ id: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ id }) =>
            linesPrefillService.remove(id).pipe(
              tap(() => {
                const currentLines = store.lines();
                const updatedLines = currentLines.filter(line => line.id !== id);
                patchState(store, {
                  lines: updatedLines,
                  period: updatedLines.length ? store.period() : null,
                  actions: updatedLines.length ? store.actions() : [],
                  isLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    return {
      ...repositoryMethods,
    };
  }),
);

export interface LinesPrefillStoreType {
  readonly lines: Signal<PrefilledLine[]>;
  readonly period: Signal<{ from: string; to: string } | null>;
  readonly actions: Signal<PrefillActions[]>;
  readonly isLoading: Signal<boolean>;

  readonly getAll: () => void;
  readonly validate: () => void;
  readonly create: (data: { data: LinePrefillRequest }) => void;
  readonly apply: () => void;
  readonly removeAll: () => void;
  readonly remove: (params: { id: string }) => void;
}
